import {
  BASKET_ELEMENT,
  OPEN_BASKET_EVENT,
  type BasketMode,
  type OpenBasketOptions,
} from "@baluu/ui/lib/checkout/basket-surface";

// The basket is per surface (plans/websitev2-booking-gaps.md B3). It was
// switched off for every embed on 2026-03-03 ("until basket issues in
// production are fixed", commit 117db2e05) by making this helper a no-op;
// the overlays have been single-item since. A surface now asks for it with
// the `basket` attribute ("on": the floating button; "page": the page shows
// its own, as Website v2's navbar does), and every other embed stays
// single-item exactly as before.

type EnsureBasketElementOptions = {
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  /** The surface's basket (`basketModeFor`): nothing is put on the page
   *  when it is off. */
  mode: BasketMode;
};

/** The school's `<baluu-basket>` on the page, if there is one. A page can
 *  hold more than one school's basket; each has its own element. */
export function basketElementFor(businessId: string): Element | null {
  if (typeof document === "undefined") return null;
  for (const el of Array.from(document.querySelectorAll(BASKET_ELEMENT))) {
    if (el.getAttribute("business-id") === businessId) return el;
  }
  return null;
}

/**
 * Puts the school's `<baluu-basket>` (its drawer) on the page when the
 * surface offers the basket. One per school: an element already there for
 * the school is reused, and asked to hide its floating button when the page
 * shows its own ("page" wins: the page's button never sits beside a second
 * one). Returns the element, or null when the basket is off.
 */
export function ensureBasketElement({
  businessId,
  apiUrl,
  primaryColor,
  mode,
}: EnsureBasketElementOptions): Element | null {
  if (mode === "off") return null;
  if (typeof document === "undefined") return null;
  const existing = basketElementFor(businessId);
  if (existing) {
    if (mode === "page") existing.setAttribute("badge", "none");
    return existing;
  }

  const el = document.createElement(BASKET_ELEMENT);
  el.setAttribute("business-id", businessId);
  if (apiUrl) el.setAttribute("api-url", apiUrl);
  if (primaryColor) el.setAttribute("primary-color", primaryColor);
  if (mode === "page") el.setAttribute("badge", "none");
  document.body.appendChild(el);
  return el;
}

// Opening a school's drawer. The drawer listens once its element has
// mounted, a frame or two after it was put on the page, so a request made
// before then waits for it — for that school's drawer only, and only
// briefly: a request nobody answered soon is stale, and must never open a
// drawer the visitor didn't just ask for.
export const PENDING_DRAWER_MS = 3000;

/**
 * Where the drawer stacks when it opens while an overlay is on the page —
 * "Open basket" from a refused add, which keeps the overlay open beneath it;
 * the basket checkout's "Review basket" with a listing overlay still mounted
 * under it; an add from a checkout opened over the timetable overlay: just
 * above the overlay hosts (`position: fixed; z-index: 9998`/`9999`, set in
 * index.ts), so it is never hidden behind one — the drawer asks
 * `overlayHostMounted` (./overlay-hosts) as it opens. Otherwise the drawer
 * keeps BasketDrawer's default.
 */
export const DRAWER_ABOVE_OVERLAYS_Z = 10001;

const listening = new Map<string, number>();
const pending = new Map<string, number>();

type OpenDrawerDetail = { businessId?: string };

/** Ask the school's basket drawer to open (now, or as soon as it listens). */
export function requestBasketDrawer(businessId: string): void {
  if (typeof window === "undefined") return;
  if ((listening.get(businessId) ?? 0) > 0) {
    window.dispatchEvent(new CustomEvent<OpenDrawerDetail>(OPEN_BASKET_EVENT, { detail: { businessId } }));
  } else {
    pending.set(businessId, Date.now());
  }
}

/** The drawer's side: open on a request for this school (or one naming no
 *  school, from an older page), and on one made shortly before it listened.
 *  Returns the unsubscribe. */
export function listenForBasketDrawer(businessId: string, open: () => void): () => void {
  const onRequest = (event: Event) => {
    const asked = (event as CustomEvent<OpenDrawerDetail | null>).detail?.businessId;
    if (!asked || asked === businessId) open();
  };
  window.addEventListener(OPEN_BASKET_EVENT, onRequest);
  listening.set(businessId, (listening.get(businessId) ?? 0) + 1);
  const askedAt = pending.get(businessId);
  pending.delete(businessId);
  if (askedAt !== undefined && Date.now() - askedAt <= PENDING_DRAWER_MS) open();
  return () => {
    window.removeEventListener(OPEN_BASKET_EVENT, onRequest);
    const left = (listening.get(businessId) ?? 1) - 1;
    if (left > 0) listening.set(businessId, left);
    else listening.delete(businessId);
  };
}

/**
 * Open the basket on the page: the school's drawer (put on the page first if
 * it isn't), from a page's own basket button. `window.BaluuWidgets.openBasket`
 * — how Website v2's navbar opens it.
 */
export function openBasket({ businessId, apiUrl, primaryColor, mode = "page" }: OpenBasketOptions): void {
  ensureBasketElement({ businessId, apiUrl, primaryColor, mode });
  requestBasketDrawer(businessId);
}

/** For the tests. */
export function resetBasketDrawerForTests(): void {
  listening.clear();
  pending.clear();
}
